import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { useEffect, useState } from "react";
import { useLoading } from "../GlobalLoading/LoadingContext";
import { useLoadingManager, LOADING_CONFIGS } from '../../hooks/useLoadingManager';
import ProfileCompletionModal from '../Profile/ProfileCompletionModal';
import debug from '../../utils/debug';

const ProtectedRoute = () => {
  const {
    currentUser,
    isVerified,
    userType,
    authReady,
    navigationReady,
    authToken,
    isAdmin
  } = useAuth();
  const location = useLocation();
  const { setIsLoading } = useLoading();
  const { startLoading, endLoading } = useLoadingManager();
  
  const [stabilizedLoading, setStabilizedLoading] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);
  const [isFirstRender, setIsFirstRender] = useState(true);
  const hasAuthToken = !!authToken;

  const logRouteCheck = (stage, details = {}) => {
    debug.log('ProtectedRoute', `[${stage}]`, {
      userUID: currentUser?.uid,
      isVerified,
      userType,
      isAdmin,
      authReady,
      navigationReady,
      hasAuthToken,
      pathname: location.pathname,
      stabilizedLoading,
      authChecked,
      ...details,
    });
  };

  // Check for admin routes
  const isAdminRoute = location.pathname.startsWith("/admin-");

  // Handle auth readiness
  useEffect(() => {
    debug.component.mount('ProtectedRoute');
    
    if (!authReady) return;

    const isBlueprintRoute =
      location.pathname.startsWith("/talent-blueprint") ||
      location.pathname.startsWith("/organiser-blueprint");
      
    const isDashboardRoute = 
      location.pathname.startsWith("/talent-dashboard") ||
      location.pathname.startsWith("/organiser-dashboard");
    
    const shouldShowLoading = isFirstRender || !hasAuthToken;
    
    if (!shouldShowLoading) {
      logRouteCheck("Skipping loading state - cached auth token available");
      setStabilizedLoading(false);
      setAuthChecked(true);
      setIsLoading(false);
      return;
    }

    if ((isBlueprintRoute || isDashboardRoute || isAdminRoute) && currentUser) {
      setStabilizedLoading(false);
      setAuthChecked(true);
      setIsLoading(false);
      setIsFirstRender(false);
    }
    
    return () => {
      setIsLoading(false);
      debug.component.unmount('ProtectedRoute');
    };
  }, [authReady, currentUser, hasAuthToken, isFirstRender, location.pathname, setIsLoading, isAdminRoute]);

  // Handle loading state timer
  useEffect(() => {
    if (!authReady) return;

    const timer = setTimeout(() => {
      logRouteCheck("Stabilizing loading state");
      setStabilizedLoading(false);
      setAuthChecked(true);
      setIsLoading(false);
      setIsFirstRender(false);
    }, 300);

    return () => {
      clearTimeout(timer);
    };
  }, [authReady, setIsLoading]);

  if ((isFirstRender || !hasAuthToken) && (!authChecked || stabilizedLoading)) {
    debug.log('ProtectedRoute', 'Showing loading state', {
      authChecked,
      stabilizedLoading
    });
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        width: '100vw',
        backgroundColor: '#f5f5f5',
        fontFamily: 'Montserrat, sans-serif'
      }}>
        <div style={{ textAlign: 'center' }}>
          <h3 style={{ color: '#144874', fontSize: "clamp(1.2rem, 2vw, 1.5rem)" }}>
            Loading...
          </h3>
          <p style={{ color: '#666', fontSize: "clamp(0.9rem, 1.5vw, 1rem)" }}>
            Preparing your profile
          </p>
        </div>
      </div>
    );
  }

  const isBlueprintRoute =
    location.pathname.startsWith("/talent-blueprint") ||
    location.pathname.startsWith("/organiser-blueprint");
    
  const isDashboardRoute =
    location.pathname.startsWith("/talent-dashboard") ||
    location.pathname.startsWith("/organiser-dashboard");

  // Handle admin routes specially
  if (isAdminRoute) {
    if (!currentUser) {
      logRouteCheck("No Current User for Admin Route", { redirect: "/admin-login" });
      return <Navigate to="/admin-login" replace />;
    }
    
    if (!isAdmin) {
      logRouteCheck("Not Admin User", { redirect: "/admin-login" });
      return <Navigate to="/admin-login" replace />;
    }
    
    // Admin users don't need verification check or user type check
    logRouteCheck("Rendering Admin Protected Route", { hasAuthToken });
    return <Outlet />;
  }

  if (!currentUser) {
    logRouteCheck("No Current User", { redirect: "/login" });
    return <Navigate to="/login" replace />;
  }

  // Only redirect to verification if the user is truly not verified
  // This is the critical condition that prevents redirect loops
  if (!isVerified && currentUser && !currentUser.emailVerified) {
    logRouteCheck("Not Verified", {
      redirect: `/verify-email?userUID=${currentUser.uid}&type=${userType || "Talent"}`,
      emailVerified: currentUser.emailVerified
    });
    
    // Use immediate loading for auth redirect
    startLoading('auth-redirect', 'Redirecting to email verification...', LOADING_CONFIGS.AUTH);
    
    return (
      <Navigate
        to={`/verify-email?userUID=${currentUser.uid}&type=${userType || "Talent"}`}
        replace
      />
    );
  }

  if (!userType && !isBlueprintRoute && !isDashboardRoute) {
    logRouteCheck("No User Type", { redirect: "/login" });
    return <Navigate to="/login" replace />;
  }

  logRouteCheck("Rendering Protected Route", { hasAuthToken });
  // Post-login profile-completeness notice (dismissible; reappears next login until
  // the profile is complete). The modal self-guards on userType/verification.
  return (
    <>
      <ProfileCompletionModal />
      <Outlet />
    </>
  );
};

export default ProtectedRoute;